import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Empty, Spin, Segmented, Row, Col, Button, Popconfirm, message } from 'antd';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { HistoryOutlined, LineChartOutlined, DeleteOutlined } from '@ant-design/icons';
import { SurveyRecord, DIMENSION_COLORS, DIMENSION_NAMES } from '../types';
import HappinessWheel from '../components/HappinessWheel';
import * as api from '../api';

const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<SurveyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'wheel' | 'trend'>('wheel');

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const sv = await api.getSurveys();
      setHistory(sv);
    } catch (err: any) {
      message.error('加载历史记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deleteSurvey(id);
      message.success('已删除记录');
      loadHistory();
    } catch {
      message.error('删除失败');
    }
  };

  const selectedRecords = history.filter(r => selectedIds.includes(r.id));

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={24}>
        {/* Left: history list */}
        <Col xs={24} lg={10}>
          <Card
            title={<><HistoryOutlined /> 历史记录</>}
            extra={
              <span style={{ fontSize: 13, color: '#999' }}>
                共 {history.length} 次记录
              </span>
            }
          >
            {history.length === 0 ? (
              <Empty description="还没有填写记录" />
            ) : (
              <List
                dataSource={history}
                renderItem={(record) => (
                  <List.Item
                    key={record.id}
                    style={{
                      cursor: 'pointer',
                      background: selectedIds.includes(record.id) ? '#e6f7ff' : undefined,
                      padding: '8px 12px',
                      borderRadius: 6,
                    }}
                    onClick={() => {
                      if (selectedIds.includes(record.id)) {
                        setSelectedIds(selectedIds.filter(id => id !== record.id));
                      } else if (selectedIds.length < 6) {
                        setSelectedIds([...selectedIds, record.id]);
                      } else {
                        message.info('最多选择6次记录对比');
                      }
                    }}
                    actions={[
                      <Popconfirm
                        key="delete"
                        title="确认删除此记录？"
                        onConfirm={(e) => { e?.stopPropagation(); handleDelete(record.id); }}
                        onCancel={(e) => e?.stopPropagation()}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <span>
                          <Tag color={selectedIds.includes(record.id) ? 'blue' : 'default'}>
                            {record.surveyDate}
                          </Tag>
                          <span style={{ fontSize: 13 }}>{record.totalScore}分</span>
                        </span>
                      }
                      description={
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                          {Object.entries(record.dimensionScores).map(([code, score]) => (
                            <Tag key={code} color={DIMENSION_COLORS[code]} style={{ fontSize: 11 }}>
                              {score}
                            </Tag>
                          ))}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
            <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
              点击记录选择/取消（最多选6个用于对比）
            </div>
          </Card>
        </Col>

        {/* Right: comparison */}
        <Col xs={24} lg={14}>
          <Card
            title={<><LineChartOutlined /> 趋势对比</>}
            extra={
              <Segmented
                value={viewMode}
                onChange={(val) => setViewMode(val as 'wheel' | 'trend')}
                options={[
                  { label: '轮图叠加', value: 'wheel', icon: <HistoryOutlined /> },
                  { label: '趋势折线', value: 'trend', icon: <LineChartOutlined /> },
                ]}
              />
            }
          >
            {selectedRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Empty description="请从左侧选择记录进行对比" />
              </div>
            ) : viewMode === 'wheel' ? (
              <div style={{ height: 400 }}>
                <HappinessWheel records={selectedRecords} />
              </div>
            ) : (
              <TrendChart records={selectedRecords} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

/** Simple trend line chart using canvas */
const TrendChart: React.FC<{ records: SurveyRecord[] }> = ({ records }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const sorted = [...records].sort((a, b) => a.surveyDate.localeCompare(b.surveyDate));

  const dimCodes = ['physical', 'mental', 'relationship', 'career', 'wealth', 'meaning'];
  const colors = dimCodes.map(c => DIMENSION_COLORS[c]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || sorted.length < 2) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const pad = { top: 30, right: 20, bottom: 40, left: 50 };
    const plotW = w - pad.left - pad.right;
    const plotH = h - pad.top - pad.bottom;

    ctx.clearRect(0, 0, w, h);

    // Grid
    ctx.strokeStyle = '#eee';
    ctx.lineWidth = 1;
    for (let y = 0; y <= 5; y++) {
      const yPos = pad.top + plotH - (y / 5) * plotH;
      ctx.beginPath();
      ctx.moveTo(pad.left, yPos);
      ctx.lineTo(w - pad.right, yPos);
      ctx.stroke();
      ctx.fillStyle = '#999';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(String(y * 3), pad.left - 6, yPos + 4);
    }

    // Y axis label
    ctx.save();
    ctx.translate(14, pad.top + plotH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = '#999';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('得分', 0, 0);
    ctx.restore();

    // Plot lines
    dimCodes.forEach((code, di) => {
      const data = sorted.map(r => r.dimensionScores[code] || 0);
      ctx.strokeStyle = colors[di];
      ctx.lineWidth = 2;
      ctx.beginPath();
      data.forEach((val, vi) => {
        const x = pad.left + (vi / (sorted.length - 1)) * plotW;
        const y = pad.top + plotH - (val / 15) * plotH;
        if (vi === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // Dots
      data.forEach((val, vi) => {
        const x = pad.left + (vi / (sorted.length - 1)) * plotW;
        const y = pad.top + plotH - (val / 15) * plotH;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = colors[di];
        ctx.fill();
      });
    });

    // X labels
    sorted.forEach((r, vi) => {
      const x = pad.left + (vi / (sorted.length - 1)) * plotW;
      ctx.fillStyle = '#666';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(r.surveyDate.slice(5), x, h - pad.bottom + 16);
    });

    // Legend
    ctx.font = '12px sans-serif';
    dimCodes.forEach((code, di) => {
      const lx = 60 + di * 80;
      const ly = 14;
      ctx.fillStyle = colors[di];
      ctx.fillRect(lx, ly - 1, 12, 3);
      ctx.fillStyle = '#666';
      ctx.textAlign = 'left';
      ctx.fillText(DIMENSION_NAMES[code], lx + 16, ly + 3);
    });
  }, [records]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: 380 }}
    />
  );
};

export default HistoryPage;
